import { isAreaFeature, type Coordinate, type EditorFeature } from '../models/editor-feature';

/**
 * Where features sit relative to each other, and how to line them up.
 *
 * Pure geometry over a selection, in the plan's own coordinates rather than on screen: an
 * alignment is a fact about the drawing, and doing it in screen space would give a different
 * answer at a different zoom.
 *
 * Y is up here, as it is everywhere in the stored data. "Top" therefore means the largest Y,
 * which is the opposite of the surface the admin is looking at — the callers pass the edge
 * they mean, and this file is where that inversion is resolved once.
 */

export interface Extent {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export type AlignEdge = 'left' | 'right' | 'top' | 'bottom' | 'centreX' | 'centreY';

export type DistributeAxis = 'x' | 'y';

/**
 * A feature's extent.
 *
 * A point feature is its own extent — zero-sized — which is what makes aligning a desk to a
 * wall's left edge put the desk *on* that edge rather than inset by half a desk.
 */
export const extentOf = (feature: EditorFeature): Extent => {
  if (!isAreaFeature(feature)) {
    const [x, y] = feature.coordinates;
    return { minX: x, minY: y, maxX: x, maxY: y };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  feature.coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  })));

  return Number.isFinite(minX)
    ? { minX, minY, maxX, maxY }
    : { minX: 0, minY: 0, maxX: 0, maxY: 0 };
};

export const unionExtent = (features: readonly EditorFeature[]): Extent | undefined => {
  if (!features.length) {
    return undefined;
  }

  return features.map(extentOf).reduce((total, extent) => ({
    minX: Math.min(total.minX, extent.minX),
    minY: Math.min(total.minY, extent.minY),
    maxX: Math.max(total.maxX, extent.maxX),
    maxY: Math.max(total.maxY, extent.maxY),
  }));
};

const edgeValue = (extent: Extent, edge: AlignEdge): number => {
  switch (edge) {
    case 'left':
      return extent.minX;
    case 'right':
      return extent.maxX;
    case 'bottom':
      return extent.minY;
    case 'top':
      return extent.maxY;
    case 'centreX':
      return (extent.minX + extent.maxX) / 2;
    default:
      return (extent.minY + extent.maxY) / 2;
  }
};

const isHorizontal = (edge: AlignEdge): boolean => edge === 'left' || edge === 'right' || edge === 'centreX';

/**
 * How far each feature must move to line up on the given edge.
 *
 * Returned as offsets rather than applied here, so the caller moves them through the same
 * mutation a drag uses — alignment is a move, and routing it anywhere else is how it would
 * end up outside the undo stack or skipping pod grouping.
 *
 * Aligned to the selection's own bounding edge, not to the first feature. With two shapes
 * either reading is defensible; with ten, "line up on the leftmost one" is what people mean,
 * and "line up on whichever I happened to click first" is a coin toss.
 */
export const alignOffsets = (
  features: readonly EditorFeature[],
  edge: AlignEdge,
): Map<EditorFeature, Coordinate> => {
  const offsets = new Map<EditorFeature, Coordinate>();
  const bounds = unionExtent(features);

  if (!bounds || features.length < 2) {
    return offsets;
  }

  const target = edgeValue(bounds, edge);
  const horizontal = isHorizontal(edge);

  features.forEach((feature) => {
    const delta = target - edgeValue(extentOf(feature), edge);

    if (delta !== 0) {
      offsets.set(feature, horizontal ? [delta, 0] : [0, delta]);
    }
  });

  return offsets;
};

/**
 * How far each feature must move for even spacing along an axis.
 *
 * The two outermost features stay where they are and everything between them is spread
 * evenly. Moving the ends as well would drift the whole row, which is never what "distribute"
 * means — the gesture is about the gaps, not the position.
 *
 * Even *centres* rather than even gaps. Gaps read better for objects of wildly different
 * sizes, but a floorplan's desks are near enough identical that the two agree, and centres
 * survive a shape being rotated where a gap does not.
 */
export const distributeOffsets = (
  features: readonly EditorFeature[],
  axis: DistributeAxis,
): Map<EditorFeature, Coordinate> => {
  const offsets = new Map<EditorFeature, Coordinate>();

  // Three is the fewest that has a middle to move. With two there is nothing between the ends.
  if (features.length < 3) {
    return offsets;
  }

  const centreOf = (feature: EditorFeature): number => {
    const extent = extentOf(feature);
    return axis === 'x' ? (extent.minX + extent.maxX) / 2 : (extent.minY + extent.maxY) / 2;
  };

  const ordered = [...features].sort((first, second) => centreOf(first) - centreOf(second));
  const first = centreOf(ordered[0]);
  const last = centreOf(ordered[ordered.length - 1]);
  const step = (last - first) / (ordered.length - 1);

  ordered.forEach((feature, index) => {
    // The ends are already where they belong, and floating-point drift on a zero move would
    // still mark the document dirty.
    if (index === 0 || index === ordered.length - 1) {
      return;
    }

    const delta = (first + step * index) - centreOf(feature);

    if (delta !== 0) {
      offsets.set(feature, axis === 'x' ? [delta, 0] : [0, delta]);
    }
  });

  return offsets;
};

export interface ArraySpec {
  readonly columns: number;
  readonly rows: number;
  /** Centre-to-centre spacing, in the plan's own units. */
  readonly spacingX: number;
  readonly spacingY: number;
}

/**
 * Where each copy of an array goes, as offsets from the original.
 *
 * The original itself is not included: it is already on the plan, and returning an offset of
 * zero for it would duplicate it in place.
 */
export const arrayOffsets = ({ columns, rows, spacingX, spacingY }: ArraySpec): Coordinate[] => {
  const offsets: Coordinate[] = [];

  for (let row = 0; row < Math.max(1, rows); row += 1) {
    for (let column = 0; column < Math.max(1, columns); column += 1) {
      // The original sits at (0, 0) and is already on the plan, so it is the one cell of the
      // grid that produces no copy.
      if (row !== 0 || column !== 0) {
        offsets.push([column * spacingX, row * spacingY]);
      }
    }
  }

  return offsets;
};

/**
 * How far a plain duplicate should sit from its original.
 *
 * Offset rather than placed exactly on top: a copy that lands perfectly over the original is
 * indistinguishable from nothing having happened, and the admin drags what they think is the
 * copy while the original stays behind it.
 */
export const duplicateOffsetOf = (features: readonly EditorFeature[]): Coordinate => {
  const bounds = unionExtent(features);

  if (!bounds) {
    return [0, 0];
  }

  // A twentieth of the selection's own size, so the nudge is visible on a desk and not absurd
  // on a whole floor plate. Floored at something non-zero for a point feature, which has no
  // size to take a fraction of.
  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  const step = Math.max(width, height) / 20;

  return step > 0 ? [step, -step] : [1, -1];
};
